// app/src/features/timeline/renderers/renderRow.jsx
// Timeline render helpers moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1. Each
// takes the column's render context first; the original parameters follow.
import { eventMidpointMs } from "../../../lib/forecast/branchTrigger.js";
import { eventKey } from "../../../lib/forecast/eventIdentity.js";
import { LABEL_MIN_PPD } from "../../../lib/timeline/geometry.js";
import { haloRadii } from "../../../lib/timeline/hitHalo.js";
import { LABEL_FADE_MS, updateLabelGhosts } from "../../../lib/timeline/labelLayout.js";
import { computeEventGeom, rowKey } from "./eventHelpers.jsx";
import { renderEvt } from "./renderEvt.jsx";
import { renderMiniDot, renderPromotedEvt } from "./renderPromotedEvt.jsx";
import { LABEL_GAP_SCALE, NEUTRAL_LABEL_GAP_SCALE, STREAM_HALF } from "./constants.js";

export function renderRow(
  ctx,
  events,
  spineY,
  color,
  forceBelow,
  forceAbove,
  animState,
  animOffset = 0,
  rowId = "main",
  hiddenEvents,
  triggerInfo,
  minSx,
  branchForkMs = null,
  // The row's forecast line and, when it draws outcome bars, their footprint
  // ({lineX, barsLeft?, halfH?} — lib/timeline/multiwayMarker.timelineMarkerFootprint).
  marker = null,
) {
  const {
    cSize,
    collapsedIds,
    computeVisibility,
    dayToScreen,
    labelGhostsRef,
    ppd,
    promotedIds,
    renderedLabelsRef,
    rowOwnerKey,
    scheduleRender,
  } = ctx;
  if (!events.length && !(hiddenEvents && hiddenEvents.length)) return null;
  const triggerEvId = triggerInfo ? triggerInfo.evId : null;
  // A neutral label box sits at least this far off the spine, whatever its circle's size
  // (renderEvt's labelY with r → 0). Bars that reach no further than this pass between the
  // labels above and below, so they only wall labels off when they are taller.
  const labelBandNear = STREAM_HALF * NEUTRAL_LABEL_GAP_SCALE * LABEL_GAP_SCALE;
  const positions = computeVisibility(events, spineY, forceBelow, forceAbove, triggerEvId, rowId, {
    capX: marker?.lineX,
    wallX: marker?.halfH > labelBandNear ? marker.barsLeft : null,
  });
  // Render-time backstop for branches: drop any non-trigger event whose final layout
  // position lands at or before the fork connector. Catches date-range events whose
  // server-anchor or slide-cache resolves to an x ≤ fSX even though the date check passed.
  const visible =
    minSx != null
      ? positions.filter((p) => p.ev.id === triggerEvId || p.sx > minSx + 1)
      : positions;
  // A label that closes fades out over its dot instead of vanishing in one frame. Below
  // LABEL_MIN_PPD no labels draw at all, so there is nothing to fade.
  if (ppd < LABEL_MIN_PPD)
    // No labels draw at this zoom, so none can be "last drawn": forget them, or zooming
    // back in would fade out labels that were never on the preceding frame.
    for (const p of visible) {
      const key = rowOwnerKey(rowId, eventKey(p.ev));
      renderedLabelsRef.current.delete(key);
      labelGhostsRef.current.delete(key);
    }
  const ghosts =
    ppd >= LABEL_MIN_PPD
      ? updateLabelGhosts(
          labelGhostsRef.current,
          visible.map((p, i) => {
            const key = rowOwnerKey(rowId, eventKey(p.ev));
            const tc = triggerInfo && triggerInfo.evId === p.ev.id;
            // "Drawn" uses renderEvt's own guards: off screen, or not yet revealed by the
            // intro animation, it returned null — a label nobody saw must not fade out.
            const onScreen = p.sx >= -60 && p.sx <= cSize.w + 60;
            const revealed = !animState || animOffset + i < animState.revealCount;
            const collapsed = collapsedIds.has(rowKey(rowId, eventKey(p.ev)));
            const dot = !(onScreen && revealed && (tc || (p.show && !collapsed)));
            // What was DRAWN last render (collapse included), not what the layout chose, so
            // a ghost starts only on a real label → dot transition.
            const lastAbove = renderedLabelsRef.current.get(key);
            // An OPEN (promoted) label draws with its own text and geometry, which the ghost
            // cannot reproduce — so it closes without one, as a click-driven change should.
            if (dot || (p.isPromoted && !tc)) renderedLabelsRef.current.delete(key);
            else renderedLabelsRef.current.set(key, p.above);
            return { key, shown: !dot, lastAbove };
          }),
          performance.now(),
        )
      : null;
  if (ghosts?.started) setTimeout(scheduleRender, LABEL_FADE_MS + 20);
  // Each pie's hit halo stops short of its neighbours' circles (lib/timeline/hitHalo.js).
  const halos = haloRadii(
    visible.map((p) => {
      const onSpine = !!triggerInfo && triggerInfo.evId === p.ev.id;
      const g = computeEventGeom(ctx, p.ev, rowId, color, p.above, spineY, onSpine);
      return { x: p.sx, y: g.circleY + g.r, r: g.r };
    }),
  );
  return (
    <>
      {ghosts &&
        visible.map((p) => {
          const ghostAbove = ghosts.draw.get(rowOwnerKey(rowId, eventKey(p.ev)));
          if (ghostAbove === undefined) return null;
          return renderEvt(
            ctx,
            p.ev,
            p.sx,
            spineY,
            color,
            ghostAbove,
            0,
            null,
            0,
            rowId,
            false,
            null,
            null,
            true,
          );
        })}
      {visible.map((p, i) => {
        const tc = triggerInfo && triggerInfo.evId === p.ev.id ? triggerInfo.ctx : null;
        const k = rowKey(rowId, eventKey(p.ev));
        if (collapsedIds.has(k) && !tc)
          return renderMiniDot(ctx, p.ev, p.sx, spineY, 0.7, "col-", p.above, rowId);
        // An open label that a more recently opened label overlaps is hidden by the overlap pass
        // and rendered as a mini dot.
        if (!p.show && !tc)
          return renderMiniDot(ctx, p.ev, p.sx, spineY, 0.7, "zh-", p.above, rowId);
        if (p.isPromoted && !tc)
          return renderPromotedEvt(
            ctx,
            p.ev,
            p.sx,
            spineY,
            color,
            p.above,
            rowId,
            branchForkMs,
            halos[i],
          );
        return renderEvt(
          ctx,
          p.ev,
          p.sx,
          spineY,
          color,
          p.above,
          animOffset + i,
          animState,
          p.stagger,
          rowId,
          p.compact || false,
          tc,
          branchForkMs,
          false,
          halos[i],
        );
      })}
      {(hiddenEvents || []).map((ev) => {
        const _evMidMs = eventMidpointMs(ev);
        const sx = _evMidMs != null ? dayToScreen(new Date(_evMidMs)) : dayToScreen(ev.date);
        if (promotedIds.has(rowKey(rowId, eventKey(ev)))) {
          const above = ev.direction === "yes";
          return renderPromotedEvt(ctx, ev, sx, spineY, color, above, rowId, branchForkMs);
        }
        return renderMiniDot(ctx, ev, sx, spineY, 0.5, "dh-", undefined, rowId);
      })}
    </>
  );
}
